// client/src/components/pages/LexiStar/PlanDispatch/UnplannedLoadPrompts.jsx
//
// Unplanned-load prompt cards (LS-09 §5.24, FR-TRIP-023): "Load detected at B,
// 10:42 — create trip?" for each open TRIP.UNPLANNED_LOAD_DETECTED event.
// Creating the trip retrospectively (POST /trips:fromDetectedLoad, origin
// 'prompted') needs a destination (M2 API: to_location_id | lane | route); when
// exactly one lane leaves the site it is pre-selected, so it stays one tap.
// "Not a load" acknowledges the event.

import React, { useState } from 'react';
import { FaPlus, FaCheck } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { fmtTime } from '../StatusBoard/format';

export const UNPLANNED_LOAD = 'TRIP.UNPLANNED_LOAD_DETECTED';

export const siteOfEvent = (evt, siteName) =>
  evt.payload?.site_name || siteName?.(evt.payload?.location_id) || 'an unplanned site';

export const registrationOfEvent = (evt) =>
  evt.registration_normalised || String(evt.asset_key || '').replace(/^reg:/, '');

function PromptCard({ evt, siteName, sites, lanes, onCreate, onDismiss, busy, canCreate }) {
  const source = evt.payload?.location_id;
  const laneTargets = lanes
    .filter((l) => String(l.from_location_id) === String(source))
    .map((l) => String(l.to_location_id));
  const [to, setTo] = useState(laneTargets.length === 1 ? laneTargets[0] : '');
  const reg = registrationOfEvent(evt);
  const known = sites.filter((s) => laneTargets.includes(String(s.location_id)));
  const others = sites.filter(
    (s) => !laneTargets.includes(String(s.location_id)) && String(s.location_id) !== String(source)
  );

  return (
    <li
      className="flex-1 min-w-[260px] max-w-md p-3 bg-surface border-2 border-dashed border-slate-400 rounded-lg"
      aria-label={`Unplanned load ${reg}`}
    >
      <p className="text-sm text-slate-900">
        <span className="font-mono font-semibold">{reg}</span>: load detected at{' '}
        <span className="font-semibold">{siteOfEvent(evt, siteName)}</span>,{' '}
        {fmtTime(evt.event_time)} — create trip?
      </p>
      {evt.payload?.ticket_ref && (
        <p className="text-xs text-slate-500 mt-0.5">Dispatch ticket {evt.payload.ticket_ref}</p>
      )}
      {canCreate && (
        <div className="mt-2">
          <ThemedSelect
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full h-9 text-sm"
            aria-label={`Delivers to (${reg})`}
          >
            <option value="">Delivers to...</option>
            {known.length > 0 && (
              <optgroup label="Lanes from this site">
                {known.map((s) => (
                  <option key={s.location_id} value={String(s.location_id)}>
                    {s.location_name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Other sites">
              {others.map((s) => (
                <option key={s.location_id} value={String(s.location_id)}>
                  {s.location_name}
                </option>
              ))}
            </optgroup>
          </ThemedSelect>
        </div>
      )}
      <div className="mt-2 flex gap-2">
        {canCreate && (
          <button
            type="button"
            onClick={() => onCreate(evt, Number(to))}
            disabled={busy || !to}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:bg-slate-400"
          >
            <FaPlus size={10} /> Create trip
          </button>
        )}
        <button
          type="button"
          onClick={() => onDismiss(evt)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          <FaCheck size={10} /> Not a load
        </button>
      </div>
    </li>
  );
}

export default function UnplannedLoadPrompts({
  events,
  siteName,
  sites = [],
  lanes = [],
  onCreate,
  onDismiss,
  busyId,
  canCreate,
}) {
  if (!events.length) return null;
  return (
    <ul className="flex flex-wrap gap-3" aria-label="Unplanned loads">
      {events.map((evt) => (
        <PromptCard
          key={evt.id}
          evt={evt}
          siteName={siteName}
          sites={sites}
          lanes={lanes}
          onCreate={onCreate}
          onDismiss={onDismiss}
          busy={busyId === evt.id}
          canCreate={canCreate}
        />
      ))}
    </ul>
  );
}
